import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

export interface TeamMember {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  role: string;
  isSuperAdmin: boolean;
  createdAt: string;
}

export interface PendingInvitation {
  id: string;
  email: string;
  expiresAt: string;
  expired: boolean;
  invitedBy: string;
  createdAt: string;
}

export function useTeamMembers() {
  return useQuery({
    queryKey: ['team'],
    queryFn: () =>
      apiFetch<{ users: TeamMember[] }>('/api/company/users').then((d) => d.users),
    staleTime: 30_000,
  });
}

export function useRemoveUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiFetch(`/api/company/users/${userId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] });
    },
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      apiFetch('/api/company/invite', { method: 'POST', body: JSON.stringify({ email }) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });
}

export function useInvitations() {
  return useQuery({
    queryKey: ['invitations'],
    queryFn: () =>
      apiFetch<{ invitations: PendingInvitation[] }>('/api/company/invitations').then((d) => d.invitations),
    staleTime: 30_000,
  });
}

export function useRevokeInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      apiFetch(`/api/company/invitations/${invitationId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...data }: { userId: string; fullName?: string; phone?: string; wechat?: string; role?: string }) =>
      apiFetch(`/api/company/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] });
    },
  });
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: (userId: string) =>
      apiFetch<{ ok: boolean; email: string }>(`/api/company/users/${userId}/reset-password`, {
        method: 'POST',
      }),
  });
}

export function useToggleSuperAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, isSuperAdmin }: { userId: string; isSuperAdmin: boolean }) =>
      apiFetch(`/api/company/users/${userId}/super-admin`, {
        method: 'PATCH',
        body: JSON.stringify({ isSuperAdmin }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] });
    },
  });
}

export function useResendInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      apiFetch(`/api/company/invitations/${invitationId}/resend`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });
}

export function useCompanyIndustries() {
  return useQuery({
    queryKey: ['company'],
    queryFn: () =>
      apiFetch<{ company: { id: string; name: string; industries: string[] } }>('/api/company').then(
        (d) => d.company,
      ),
    staleTime: 60_000,
  });
}

export function useUpdateIndustries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (industries: string[]) =>
      apiFetch<{ company: { id: string; name: string; industries: string[] } }>(
        '/api/company/industries',
        { method: 'PATCH', body: JSON.stringify({ industries }) },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company'] });
      // Also invalidate leads since industry tags may have changed
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}
